/**
 * Tiffin rules both clients need.
 *
 * Pure functions, mirroring `app/domain/services/tiffin.py` on the server. As
 * with donations, a preview is not a quote: these keep the menu responsive
 * while the server prices the order, and the total a customer confirms is
 * always the server's.
 */

import type {
  DeliveryAddress,
  FulfilmentType,
  MenuItem,
  OrderType,
  ServiceSchedule,
  ServiceSlot,
  TiffinConfig,
  TiffinCustomer,
  TiffinQuote,
} from './contracts';
import type { FieldErrors } from './donation';
import { formatDate, type Money } from './format';
import type { OrderStatus } from './status';

export const SERVICE_SLOTS: readonly ServiceSlot[] = ['BREAKFAST', 'LUNCH', 'DINNER'];

export const serviceSlotLabel: Record<ServiceSlot, string> = {
  BREAKFAST: 'Breakfast',
  LUNCH: 'Lunch',
  DINNER: 'Dinner',
};

export const SERVICE_SCHEDULES: readonly ServiceSchedule[] = [
  'DAILY',
  'TWICE_WEEKLY',
  'WEEKLY',
  'WEEKENDS',
];

export const serviceScheduleLabel: Record<ServiceSchedule, string> = {
  DAILY: 'Daily',
  TWICE_WEEKLY: 'Bi-weekly',
  WEEKLY: 'Weekly',
  WEEKENDS: 'Weekends',
};

const DAY_MS = 86_400_000;
const WEEKDAYS = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
const utc = (date: string) => Date.parse(`${date}T00:00:00Z`);

/**
 * The days in a run of `days` from `start` that a schedule delivers on —
 * the server's `delivery_dates`. Twice a week is the start day's weekday and
 * three days after it, so a Monday start delivers Mondays and Thursdays.
 */
export function deliveryDates(start: string, days: number, schedule: ServiceSchedule): string[] {
  const first = utc(start);
  const run = Array.from({ length: Math.max(0, Math.trunc(days)) }, (_, offset) => offset);
  return run
    .filter((offset) => {
      if (schedule === 'WEEKLY') return offset % 7 === 0;
      if (schedule === 'TWICE_WEEKLY') return offset % 7 === 0 || offset % 7 === 3;
      if (schedule === 'WEEKENDS') {
        const weekday = new Date(first + offset * DAY_MS).getUTCDay();
        return weekday === 0 || weekday === 6;
      }
      return true;
    })
    .map((offset) => new Date(first + offset * DAY_MS).toISOString().slice(0, 10));
}

/**
 * What a schedule means from this start day, in English for `t()` to
 * translate by its parts: `['Twice a week', 'Tuesdays', 'Fridays']`.
 */
export function scheduleDays(schedule: ServiceSchedule, start: string): string[] {
  const weekday = new Date(utc(start)).getUTCDay();
  if (schedule === 'TWICE_WEEKLY') {
    return ['Twice a week', WEEKDAYS[weekday]!, WEEKDAYS[(weekday + 3) % 7]!];
  }
  if (schedule === 'WEEKLY') return ['Once a week', WEEKDAYS[weekday]!];
  if (schedule === 'WEEKENDS') return ['Weekends', 'Saturdays', 'Sundays'];
  return ['Every day'];
}

export const ORDER_TYPES: readonly OrderType[] = ['DONATION', 'TIFFIN'];

export const orderTypeLabel: Record<OrderType, string> = {
  DONATION: 'Donation',
  TIFFIN: 'Tiffin',
};

/** "Pick & Drop" and "Self Pickup": what a customer chooses between. */
export const tiffinFulfilmentLabel: Record<FulfilmentType, string> = {
  DOORSTEP_DELIVERY: 'Pick & Drop',
  SELF_PICKUP: 'Self Pickup',
  CHARITY_DISTRIBUTION: 'Charity Distribution',
};

const TIFFIN_LABELS: Partial<Record<OrderStatus, string>> = {
  NEW: 'Order Placed',
  PAYMENT_CONFIRMED: 'Payment Confirmed',
  ACCEPTED: 'Order Accepted',
  PREPARING: 'Preparing',
  COMPLETED: 'Completed',
  PAYMENT_FAILED: 'Payment Failed',
  CANCELLED: 'Cancelled',
  REFUND_PENDING: 'Refund Pending',
  REFUNDED: 'Refunded',
};

/**
 * A tiffin order's name for a step, for its route.
 *
 * The lifecycle is the donation's, so payments and the admin's transitions
 * serve both; the words are not. Nobody's lunch is "ready for distribution".
 */
export function tiffinStatusLabel(status: OrderStatus, fulfilment: FulfilmentType): string {
  const delivered = fulfilment === 'DOORSTEP_DELIVERY';
  if (status === 'READY_FOR_DISTRIBUTION') return delivered ? 'Out for Delivery' : 'Ready for Pickup';
  if (status === 'DISTRIBUTED') return delivered ? 'Delivered' : 'Collected';
  return TIFFIN_LABELS[status] ?? status;
}

/** "12:30" → "12:30 PM", "08:00" → "8:00 AM". */
export function formatClock(hhmm: string): string {
  const [hours = 0, minutes = 0] = hhmm.split(':').map(Number);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

const NBSP = '\u00a0';

/**
 * "12:30 PM – 2:30 PM", each time held together: a window too wide for its
 * box breaks at the dash, never between "2:30" and "PM".
 */
export const formatWindow = (start: string, end: string): string =>
  `${formatClock(start).replace(' ', NBSP)} – ${formatClock(end).replace(' ', NBSP)}`;

// ------------------------------------------------------------------- pricing

export interface BoxLine {
  item: Pick<MenuItem, 'id' | 'price'>;
  quantity: number;
}

/** One box, previewed: the sum of each item's price times its quantity. */
export function previewBox(lines: BoxLine[], currency = 'PKR'): Money {
  return {
    amountMinor: lines.reduce(
      (sum, line) => sum + line.item.price.amountMinor * Math.max(0, Math.trunc(line.quantity)),
      0,
    ),
    currency: lines[0]?.item.price.currency ?? currency,
  };
}

/**
 * One box a person a delivery, plus pick & drop for each delivery. Preview
 * only: the figure charged is always the server's quote.
 */
export function previewTiffinTotal(
  box: Money,
  persons: number,
  fee?: Money | null,
  deliveries = 1,
): Money {
  const whole = (value: number) => Math.max(0, Math.trunc(value));
  return {
    amountMinor:
      box.amountMinor * whole(persons) * whole(deliveries) +
      (fee?.amountMinor ?? 0) * whole(deliveries),
    currency: box.currency,
  };
}

/** Keep a stepper inside 0..the item's own limit. */
export const clampQuantity = (value: number, maximum: number): number =>
  Math.min(Math.max(0, Math.trunc(value)), maximum);

export interface TiffinSelection {
  serviceDate: string | null;
  serviceSlot: ServiceSlot | null;
  persons: number;
  /** Consecutive days from `serviceDate`. */
  days: number;
  schedule: ServiceSchedule;
  /** menuItemId → quantity per box. */
  quantities: Record<string, number>;
  fulfilmentType: FulfilmentType | null;
}

/** The lines of a selection, zero quantities dropped. */
export function selectedLines(quantities: Record<string, number>) {
  return Object.entries(quantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([menuItemId, quantity]) => ({ menuItemId, quantity }));
}

/** True when a server quote still describes what the customer is looking at. */
export function tiffinQuoteMatches(quote: TiffinQuote, selection: TiffinSelection): boolean {
  if (
    quote.serviceDate !== selection.serviceDate ||
    quote.serviceSlot !== selection.serviceSlot ||
    quote.persons !== selection.persons ||
    quote.days !== selection.days ||
    quote.schedule !== selection.schedule ||
    quote.fulfilmentType !== selection.fulfilmentType
  ) {
    return false;
  }
  const quoted = new Map(quote.items.map((line) => [line.menuItemId, line.quantity]));
  const chosen = selectedLines(selection.quantities);
  return (
    chosen.length === quoted.size &&
    chosen.every((line) => quoted.get(line.menuItemId) === line.quantity)
  );
}

// ---------------------------------------------------------------- validation

const PK_MOBILE_RE = /^(?:\+92|0)3\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateDays(days: number, config: TiffinConfig): string | null {
  if (!Number.isInteger(days) || days < config.minDays) {
    return `Tiffin orders are for at least ${config.minDays} days.`;
  }
  if (days > config.maxDays) return `One order can cover up to ${config.maxDays} days.`;
  return null;
}

/** "30 Sept – 6 Oct, 2026"; one date when it is one day. */
export function formatDayRange(start: string, end: string): string {
  if (start === end) return formatDate(start);
  const first = formatDate(start);
  const last = formatDate(end);
  return start.slice(0, 4) === end.slice(0, 4)
    ? `${first.replace(/, \d{4}$/, '')} – ${last}`
    : `${first} – ${last}`;
}

/** The last day of a run of `days` starting on `start`. */
export function tiffinEndDate(start: string, days: number): string {
  const date = new Date(`${start}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + Math.max(1, Math.trunc(days)) - 1);
  return date.toISOString().slice(0, 10);
}

export function validatePersons(persons: number, config: TiffinConfig): string | null {
  if (!Number.isInteger(persons) || persons < 1) return 'A tiffin is for at least one person.';
  if (persons > config.maxPersons) return `One order can serve up to ${config.maxPersons} persons.`;
  return null;
}

/**
 * A customer must be reachable — unlike a donor, who may stay anonymous.
 * The kitchen calls when the rider is outside.
 */
export function validateTiffinCustomer(customer: TiffinCustomer): FieldErrors {
  const errors: FieldErrors = {};
  if ((customer.fullName ?? '').trim().length < 2) errors.fullName = 'Enter your name.';
  const mobile = (customer.mobileNumber ?? '').replace(/[\s-]/g, '');
  if (!PK_MOBILE_RE.test(mobile)) {
    errors.mobileNumber = 'Enter a valid mobile number, for example 03001234567.';
  }
  if (customer.email && !EMAIL_RE.test(customer.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  return errors;
}

export function validateDeliveryAddress(
  address: Partial<DeliveryAddress> | null,
  cities: string[],
): FieldErrors {
  const errors: FieldErrors = {};
  if ((address?.addressLine ?? '').trim().length < 4) {
    errors.addressLine = 'Enter the house and street to deliver to.';
  }
  const city = (address?.city ?? '').trim();
  if (!city) {
    errors.city = 'Choose the city.';
  } else if (!cities.some((served) => served.toLowerCase() === city.toLowerCase())) {
    errors.city = `We deliver to ${cities.join(' or ')} only.`;
  }
  return errors;
}
